import { isValid, parseISO } from 'date-fns';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { TimelineRecord } from 'src/types/convocation-summary';
import { UQBAR_DATE_FORMAT, UQBAR_TIMEZONE } from 'src/utils/date';

dayjs.extend(utc);
dayjs.extend(timezone);

export type TimelineAuxiliaryCount = number | 'loading' | 'unavailable';
export type TimelineTab = 'highlights' | 'future' | 'all';
export type TimelineSortOption = 'materiality' | 'recent' | 'oldest';

// Eventos futuros começa pelo mais próximo de acontecer; as demais abas, pelo mais recente.
export const getTimelineDefaultSort = (tab: TimelineTab): TimelineSortOption =>
  tab === 'future' ? 'oldest' : 'recent';

export type TimelineFilters = {
  markets: string[];
  categories: string[];
  types: string[];
  species: string[];
  themes: string[];
  relevances: string[];
  dateStart: string;
  dateEnd: string;
  favorites: boolean;
};

export const EMPTY_TIMELINE_FILTERS: TimelineFilters = {
  markets: [],
  categories: [],
  types: [],
  species: [],
  themes: [],
  relevances: [],
  dateStart: '',
  dateEnd: '',
  favorites: false,
};

export const getTimelineToday = () =>
  dayjs().tz(UQBAR_TIMEZONE).format(UQBAR_DATE_FORMAT);

export const getTimelineTomorrow = () =>
  dayjs().tz(UQBAR_TIMEZONE).add(1, 'day').format(UQBAR_DATE_FORMAT);

export const isTimelineDateAfterToday = (date?: string | null) => {
  if (!date) return false;

  const isCivilDate = /^\d{4}-\d{2}-\d{2}$/.test(date);
  const parsedDate = parseISO(date);

  if (!isValid(parsedDate)) return false;

  const calendarDate = isCivilDate
    ? date
    : dayjs(parsedDate).tz(UQBAR_TIMEZONE).format(UQBAR_DATE_FORMAT);

  return calendarDate > getTimelineToday();
};

type TimelineCountQuery = {
  data?: { meta?: { pagination?: { total?: number } } };
  isLoading: boolean;
  isError: boolean;
};

export const getTimelineAuxiliaryCount = ({
  data,
  isLoading,
  isError,
}: TimelineCountQuery): TimelineAuxiliaryCount => {
  if (isLoading) return 'loading';
  if (isError) return 'unavailable';
  return typeof data?.meta?.pagination?.total === 'number'
    ? data.meta.pagination.total
    : 'unavailable';
};

export const getTimelineRecordsFromResponse = ({
  records,
  isError,
}: {
  records?: TimelineRecord[];
  isError: boolean;
}) => (isError ? [] : records ?? []);

export const shouldShowTimelineEmptyState = ({
  isLoading,
  isError,
  visibleCount,
}: {
  isLoading: boolean;
  isError: boolean;
  visibleCount: number;
}) => !isLoading && !isError && visibleCount === 0;
